import { GRPC_STREAMING_TIMEOUT } from './constants';
import { getErrorMessage } from './getErrorMessage';
import { handleAndThrowForBotTraffic } from './handleAndThrowForBotTraffic';
import { logGrpcClientWarn } from './logGrpcClientWarn';
import { mapGrpcToHttpStatusCode } from './mapGrpcErrorCodes';

import type {
  InitReq,
  NotifyStreamEntityArrival,
} from '@skyscanner-internal/web-http-client-fetch/fetch.pb';

const GRPC_STREAMING_LOGGING_COMPONENT = 'grpcStreamingClientWrapper';

export type GrpcStreamingClientWrapperOptions = {
  clientName: string;
  pathPrefix: string;
  timeoutMs?: number;
};

export type GrpcStreamingRequestMethod<Req, Res> = (
  request: Req,
  entityNotifier?: NotifyStreamEntityArrival<Res>,
  initRequest?: InitReq,
) => Promise<void>;

const extractGrpcErrorDetails = (error: unknown): any => {
  if (error instanceof Error && error.cause && typeof error.cause === 'object') {
    return error.cause;
  }

  return error;
};

const requestTimeout = (timeoutMs: number) => {
  const controller = new AbortController();
  const { promise: timeoutPromise, reject } = Promise.withResolvers<never>();

  const id = setTimeout(() => {
    controller.abort();
    reject(new DOMException('The operation was aborted.', 'AbortError'));
  }, timeoutMs);

  return {
    clearRequestTimeout: () => clearTimeout(id),
    signal: controller.signal,
    timeoutPromise,
  };
};

const makeStreamingRequest = async <Req extends Record<string, unknown>, Res>({
  body,
  clientName,
  headers,
  onEntity,
  pathPrefix,
  requestMethod,
  timeoutMs = GRPC_STREAMING_TIMEOUT,
}: {
  body: Req;
  clientName: string;
  headers: InitReq['headers'];
  onEntity: NotifyStreamEntityArrival<Res>;
  pathPrefix: string;
  requestMethod: GrpcStreamingRequestMethod<Req, Res>;
  timeoutMs?: number;
}): Promise<void> => {
  const { clearRequestTimeout, signal, timeoutPromise } = requestTimeout(timeoutMs);

  try {
    await Promise.race([
      requestMethod(body, onEntity, {
        headers,
        pathPrefix,
        signal,
      }),
      timeoutPromise,
    ]);
  } catch (error: unknown) {
    const errorDetails = extractGrpcErrorDetails(error);

    handleAndThrowForBotTraffic(errorDetails);

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }

    const httpStatusCode = mapGrpcToHttpStatusCode(errorDetails.code);

    if (httpStatusCode) {
      logGrpcClientWarn({
        body,
        clientName,
        component: GRPC_STREAMING_LOGGING_COMPONENT,
        error: errorDetails,
        headers,
        httpStatusCode,
        message: `Request failed with gRPC error code ${errorDetails.code}`,
      });
    } else {
      logGrpcClientWarn({
        body,
        clientName,
        component: GRPC_STREAMING_LOGGING_COMPONENT,
        error: errorDetails,
        headers,
        message: `Request failed. Status code undefined. Error: ${getErrorMessage(error)}`,
      });
    }

    throw error;
  } finally {
    clearRequestTimeout();
  }
};

export const grpcStreamingClientWrapper =
  <Req extends Record<string, unknown>, Res>(
    requestMethod: GrpcStreamingRequestMethod<Req, Res>,
    { clientName, pathPrefix, timeoutMs }: GrpcStreamingClientWrapperOptions,
  ) =>
  async ({
    body,
    headers,
    onEntity,
  }: {
    body?: Req;
    headers: InitReq['headers'];
    onEntity: NotifyStreamEntityArrival<Res>;
  }): Promise<void> => {
    if (!body) {
      const message = 'Request failed: request body undefined';
      const error = new Error(`${clientName}: ${message}`);

      logGrpcClientWarn({
        body,
        clientName,
        component: GRPC_STREAMING_LOGGING_COMPONENT,
        error,
        headers,
        message,
      });

      throw error;
    }

    return makeStreamingRequest({
      body,
      clientName,
      headers,
      onEntity,
      pathPrefix,
      requestMethod,
      timeoutMs,
    });
  };
